// InteliChat routing policy - ADDITIVE guard. Opening a single
// conversation ANYWHERE (email/push deep-links, notification clicks,
// folder/label/team/mentions/unattended/participating lists, stale
// bookmarks) defaults into the InteliChat IN-SHELL conversation
// experience (intelichat/inbox/:conversationId) instead of the raw
// native Chatwoot conversation screen. The conversation id + query are
// preserved. No native route is removed or rewritten.
//
// Classic stays reachable, deliberately, via an EXPLICIT bypass only:
// any URL carrying ?classic=1 stays native. (Native conversation routes
// still exist and render normally when reached with that flag.)
//
// Match strategy: by the CONVERSATION-ID PARAM, not a route-name list.
// Every native conversation-DETAIL route carries a :conversation_id
// (plain/inbox/label/folder) or :conversationId (team/mentions/
// unattended/participating) param; nothing else in the app does except
// our own intelichat_inbox target, which is excluded below. This is
// robust to new native conversation routes and cannot over-catch
// settings / reports / contacts / the folder-and-label LIST routes
// (none of which carry a conversation id).
//
// Loop-safe: our own intelichat_* routes are never matched (the
// namespace is excluded), and the InteliChat workspaces open
// conversations via setActiveChat (no native route push), so no
// ping-pong is possible.

export const intelichatConversationRedirect = to => {
  // Never yank our own InteliChat routes. intelichat_inbox itself
  // carries a :conversationId param, so exclude the whole namespace.
  if (typeof to?.name === 'string' && to.name.startsWith('intelichat')) {
    return null;
  }
  const conversationId =
    to?.params?.conversation_id || to?.params?.conversationId;
  if (!conversationId) return null; // not a single-conversation route
  if (to.query?.classic) return null; // explicit Classic-view bypass
  // preserve the query (e.g. ?messageId= scroll-to-message deep-links)
  // minus the bypass flag.
  const query = { ...(to.query || {}) };
  delete query.classic;
  return {
    name: 'intelichat_inbox',
    params: { accountId: to.params.accountId, conversationId },
    query,
    replace: true,
  };
};

// The all-chats HISTORY lookup (intelichat_history) embeds the NATIVE
// SearchView, which persists its query/tab/filter state via
// router.replace({ name: 'search', ... }) on every search/tab/filter
// change. Fold those writes - and any native search deep-link - back
// into the InteliChat history route so the lookup stays in-shell with
// the History nav lit. The route shapes are identical
// (search/:tab? <-> intelichat/history/:tab?), so params + query
// (q, date/agent/inbox filters) are preserved 1:1. Same explicit
// ?classic=1 bypass keeps the native search screen reachable.
// Loop-safe: the target route is named intelichat_history, which this
// matcher (name === 'search') can never re-match.
export const intelichatSearchRedirect = to => {
  if (to?.name !== 'search') return null;
  if (to.query?.classic) return null; // explicit Classic-view bypass
  const params = { accountId: to.params.accountId };
  if (to.params.tab) params.tab = to.params.tab;
  return {
    name: 'intelichat_history',
    params,
    query: to.query || {},
    replace: true,
  };
};

export default { intelichatConversationRedirect, intelichatSearchRedirect };
